import { useMemo, useState } from 'react';

type StepId = string;

export type Step = {
  id: StepId;
  order: number;
};

export type UseStepperProps = {
  steps: Step[];
  initialStep: StepId;
};

function byStepId(stepId: StepId) {
  return (step: Step) => {
    return step.id === stepId;
  };
}

function sortByOrder(stepOne: Step, stepTwo: Step) {
  return stepOne.order - stepTwo.order;
}

function getId(step: Step) {
  return step.id;
}

export function useStepper(props: UseStepperProps) {
  const indexes = useMemo(() => props.steps.sort(sortByOrder).map(getId), [props]);
  const [currentStep, setCurrentStep] = useState(() => props.steps.find(byStepId(props.initialStep)));

  function nextStep() {
    if (currentStep && indexes) {
      const nextIndex = indexes.indexOf(currentStep.id) + 1;

      if (nextIndex >= indexes.length) {
        return;
      }

      const nextStep = props.steps[nextIndex];
      setCurrentStep(nextStep);
    }
  }

  function goToStep(stepId: StepId) {
    const step = props.steps.find(byStepId(stepId));

    if (!step) {
      throw new Error(`Step Id "${stepId}" is not 
      registered`);
    }

    if (step) {
      setCurrentStep(step);
    }
  }

  function prevStep() {
    if (currentStep && indexes) {
      const prevIndex = indexes.indexOf(currentStep.id) - 1;

      if (prevIndex < 0) {
        return;
      }

      const prevStep = props.steps[prevIndex];
      setCurrentStep(prevStep);
    }
  }

  function isCurrentStep(stepId: StepId) {
    if (currentStep) {
      return stepId === currentStep.id;
    }
  }

  return {
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    isCurrentStep
  };
}
